import { Flex, Badge, Text, Button } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import TableComponent from '../components/ui-components/TableComponent';
import CreateGateway from '../components/ui-components/CreateGateway';
import ViewGateway from '../components/ui-components/ViewGateway';
import SearchFilter from '../components/ui-components/SearchFilter';
import PaginationComponent from '../components/ui-components/PaginationComponent';
import ExcelUploadComponent from '../components/ui-components/ExcelUploadComponent';
import { MdOutlineEdit, MdOutlineVerifiedUser } from 'react-icons/md';
import axiosInstance from '../config/axiosConfig';
import debounce from 'lodash/debounce';
import { highlightSearchTerm } from '../utils/helpers';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const GatewayPage = () => {
  const [gateways, setGateways] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const pageSize = 8;

  const columns = [
    { title: '#', key: 'serialNumber', width: '5%', render: (value) => <span>{value}</span> },
    {
      title: 'Gateway Address',
      key: 'gateway_address',
      width: '25%',
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: 'IGIN',
      key: 'igin',
      width: '20%',
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: 'IGVN',
      key: 'igvn',
      width: '20%',
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: 'Status',
      key: 'isActive',
      width: '15%',
      render: (value) => (
        <Badge color={value ? 'green' : 'tomato'}>
          <MdOutlineVerifiedUser color={value ? 'green' : 'red'} />
          {value ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, row) => (
        <Flex gap={'2'} align={'center'}>
          <Flex direction={'row'} gap={'1'}>
            <CreateGateway initialValues={row} onGatewayCreated={fetchGateways} showToast={showToast} />
            <ViewGateway initialValues={row} onGatewayUpdated={fetchGateways} showToast={showToast} />
          </Flex>
        </Flex>
      ),
    },
  ];

  const fetchGateways = useCallback(async (page = 1, query = searchQuery, activeOnly = showActiveOnly) => {
    try {
      const response = await axiosInstance.get('/gateway/get-gateways', {
        params: {
          token: null,
          pageNumber: page,
          pageSize,
          isSuperAdmin: true,
          searchQuery: query.length >= 3 ? query : null,
          isActive: activeOnly ? true : null,
        },
      });
      if (response.data && response.data.result) {
        const gatewayData = response.data.result.gatewayList.map((item, index) => ({
          serialNumber: (page - 1) * pageSize + index + 1,
          token: item.token,
          gateway_address: item.gatewayAddress,
          igin: item.igin,
          igvn: item.igvn,
          isActive: item.isActive,
        }));
        setGateways(gatewayData);
        setTotalCount(response.data.result.totalCount || 0);
      } else {
        setGateways([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching gateways:', error);
      setGateways([]);
      setTotalCount(0);
      showToast('Failed to fetch gateways', 'error');
    }
  }, [searchQuery, showActiveOnly]);

  const debouncedFetch = useCallback(debounce((page, query, activeOnly) => {
    fetchGateways(page, query, activeOnly);
  }, 300), [fetchGateways]);

  useEffect(() => {
    debouncedFetch(currentPage, searchQuery, showActiveOnly);
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch]);

  const handleSearchChange = () => {
    setCurrentPage(1);
  };

  const handleActiveToggle = () => {
    setCurrentPage(1);
  };

  const showToast = (message, type = 'success') => {
    if (type === 'error') {
      toast.error(message);
    } else {
      toast.success(message);
    }
  };

  const handleUploadSuccess = () => {
    // Refresh the gateways list after successful upload
    fetchGateways(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" mb="4" gap="3" wrap="wrap">
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly}
            placeholder="Search gateways (min 3 chars)"
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
          <Flex gap="3" align="center">
            {/* Excel Upload Component */}
            <ExcelUploadComponent 
              type="gateway" 
              onUploadSuccess={handleUploadSuccess}
              showToast={showToast}
            />
            {/* Create Gateway Button */}
            <CreateGateway onGatewayCreated={() => fetchGateways(1)} showToast={showToast} />
          </Flex>
        </Flex>
        <TableComponent dataStructure={gateways} columns={columns} />
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        )}
        {totalCount > 0 && (
          <Text size="2" align="center" mt="2">
            Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} gateways
          </Text>
        )}
      </Flex>
    </>
  );
};

export default GatewayPage;